import { useRef, useEffect, useState, FormEvent } from 'react';
import TermsModal from './TermsModal';
import PrivacyModal from './PrivacyModal';

const FORM_URL = 'https://readdy.ai/api/form/d6sucbjvrivh3q8km82g';

const COUPON_CODE = 'WELCOMEWITHIN10';

const benefits = [
  { icon: 'ri-gift-line', title: '신규 회원 혜택', desc: '가입 즉시 10% 할인 쿠폰 지급' },
  { icon: 'ri-notification-3-line', title: '신제품 알림', desc: '출시 전 회원 전용 사전 안내' },
];

const inputClass = 'w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#C4A882] transition-colors';
const labelClass = 'block text-white/50 text-xs mb-1.5';

export default function ContactSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [marketingAgreed, setMarketingAgreed] = useState(false);
  const [gender, setGender] = useState('');
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleCopy = () => {
    navigator.clipboard.writeText(COUPON_CODE).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!agreed) return;
    setSubmitting(true);
    const data = new URLSearchParams();
    const formData = new FormData(e.currentTarget);
    formData.forEach((value, key) => { data.append(key, value as string); });
    if (gender) data.append('gender', gender);
    data.append('marketing_agree', marketingAgreed ? '동의' : '미동의');

    try {
      await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="bg-[#2C2825] py-24 px-6" ref={sectionRef}>
      <div className="max-w-6xl mx-auto">
        <div className={`text-center mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <p className="text-[#C4A882] text-xs font-medium tracking-[0.3em] uppercase mb-3">Membership</p>
          <h2 className="text-white text-4xl lg:text-5xl font-light mb-4">
            회원가입
          </h2>
          <p className="text-white/50 text-base">
            위딘 멤버가 되어 다양한 혜택을 누려보세요.<br />지금 가입하면 10% 할인 쿠폰을 바로 드립니다.
          </p>
        </div>

        <div className={`grid grid-cols-1 lg:grid-cols-5 gap-8 transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {/* Left: Benefits */}
          <div className="lg:col-span-2 space-y-5">
            {benefits.map((b) => (
              <div key={b.title} className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/10 flex-shrink-0">
                  <i className={`${b.icon} text-[#C4A882] text-lg`}></i>
                </div>
                <div>
                  <p className="text-white text-sm font-medium mb-0.5">{b.title}</p>
                  <p className="text-white/40 text-xs leading-relaxed">{b.desc}</p>
                </div>
              </div>
            ))}
            <div className="pt-6 border-t border-white/10">
              <p className="text-white/30 text-xs leading-relaxed">
                이미 회원이신가요?{' '}
                <span className="text-[#C4A882] underline underline-offset-2 cursor-pointer">
                  로그인하기
                </span>
              </p>
            </div>
          </div>

          {/* Right: Form */}
          <div className="lg:col-span-3 bg-white/5 rounded-2xl p-8 border border-white/10">
            {submitted ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-10">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#8B7355]/20 mb-4">
                  <i className="ri-check-line text-3xl text-[#C4A882]"></i>
                </div>
                <h3 className="text-white text-xl font-light mb-2">
                  가입이 완료되었습니다
                </h3>
                <p className="text-white/50 text-sm mb-8">위딘 멤버가 되신 것을 환영합니다!</p>

                {/* 쿠폰 카드 */}
                <div className="w-full max-w-sm border border-dashed border-[#C4A882]/60 rounded-2xl p-6 bg-[#C4A882]/5">
                  <p className="text-[#C4A882] text-xs font-medium tracking-[0.2em] uppercase mb-1">신규 회원 전용</p>
                  <p className="text-white text-2xl font-semibold mb-1">10% 할인 쿠폰</p>
                  <p className="text-white/40 text-xs mb-5">구매 시 쿠폰 코드를 입력해주세요</p>

                  <div className="flex items-center gap-2 bg-white/10 rounded-xl px-4 py-3 mb-3">
                    <span className="text-white text-lg font-mono font-bold tracking-widest flex-1 text-center">
                      {COUPON_CODE}
                    </span>
                    <button
                      type="button"
                      onClick={handleCopy}
                      className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#8B7355] hover:bg-[#7A6548] transition-colors cursor-pointer flex-shrink-0"
                    >
                      <i className={`${copied ? 'ri-check-line' : 'ri-file-copy-line'} text-white text-sm`}></i>
                    </button>
                  </div>

                  {copied && (
                    <p className="text-[#C4A882] text-xs text-center animate-pulse">쿠폰 코드가 복사되었습니다!</p>
                  )}
                  {!copied && (
                    <p className="text-white/30 text-xs text-center">위 코드를 복사해 결제 시 사용하세요</p>
                  )}
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-readdy-form>
                {/* 이름 + 연락처 */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className={labelClass}>이름 *</label>
                    <input type="text" name="name" required placeholder="홍길동" className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>연락처 *</label>
                    <input type="tel" name="phone" required placeholder="010-0000-0000" className={inputClass} />
                  </div>
                </div>

                {/* 이메일 */}
                <div className="mb-4">
                  <label className={labelClass}>이메일 *</label>
                  <input type="email" name="email" required placeholder="example@email.com" className={inputClass} />
                </div>

                {/* 비밀번호 */}
                <div className="mb-4">
                  <label className={labelClass}>비밀번호 *</label>
                  <input type="password" name="password" required placeholder="8자 이상 입력해주세요" minLength={8} className={inputClass} />
                </div>

                {/* 생년월일 + 성별 */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className={labelClass}>생년월일 *</label>
                    <input
                      type="date"
                      name="birth_date"
                      required
                      className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:border-[#C4A882] transition-colors [color-scheme:dark]"
                    />
                  </div>
                  <div>
                    <label className={labelClass}>성별 *</label>
                    <div className="flex gap-2 mt-1">
                      {['남성', '여성', '선택 안 함'].map((g) => (
                        <button
                          key={g}
                          type="button"
                          onClick={() => setGender(g)}
                          className={`flex-1 py-3 rounded-lg text-xs font-medium border transition-colors cursor-pointer whitespace-nowrap ${
                            gender === g
                              ? 'bg-[#8B7355] border-[#8B7355] text-white'
                              : 'bg-white/10 border-white/20 text-white/50 hover:border-[#C4A882] hover:text-white/80'
                          }`}
                        >
                          {g}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* 약관 동의 */}
                <div className="space-y-3 mb-6 pt-4 border-t border-white/10">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <button
                      type="button"
                      onClick={() => setAgreed(!agreed)}
                      className={`mt-0.5 w-5 h-5 flex-shrink-0 rounded flex items-center justify-center border transition-colors cursor-pointer ${agreed ? 'bg-[#8B7355] border-[#8B7355]' : 'bg-transparent border-white/30'}`}
                    >
                      {agreed && <i className="ri-check-line text-white text-xs"></i>}
                    </button>
                    <span className="text-white/40 text-xs leading-relaxed">
                      <span
                        className="text-[#C4A882] underline underline-offset-2 cursor-pointer hover:text-[#D4B892] transition-colors"
                        onClick={() => setShowTerms(true)}
                      >이용약관</span> 및{' '}
                      <span
                        className="text-[#C4A882] underline underline-offset-2 cursor-pointer hover:text-[#D4B892] transition-colors"
                        onClick={() => setShowPrivacy(true)}
                      >개인정보 처리방침</span>에 동의합니다. (필수)
                    </span>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <button
                      type="button"
                      onClick={() => setMarketingAgreed(!marketingAgreed)}
                      className={`mt-0.5 w-5 h-5 flex-shrink-0 rounded flex items-center justify-center border transition-colors cursor-pointer ${marketingAgreed ? 'bg-[#8B7355] border-[#8B7355]' : 'bg-transparent border-white/30'}`}
                    >
                      {marketingAgreed && <i className="ri-check-line text-white text-xs"></i>}
                    </button>
                    <span className="text-white/40 text-xs leading-relaxed">
                      신제품 출시, 이벤트, 건강 정보 등 마케팅 정보 수신에 동의합니다. (선택)
                    </span>
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={submitting || !agreed || !gender}
                  className="w-full bg-[#8B7355] text-white py-3.5 rounded-full text-sm font-medium hover:bg-[#7A6548] transition-colors duration-300 cursor-pointer whitespace-nowrap disabled:opacity-40"
                >
                  {submitting ? '처리 중...' : '회원가입 완료'}
                </button>
                {!gender && (
                  <p className="text-white/30 text-xs text-center mt-2">성별을 선택해야 가입이 완료됩니다.</p>
                )}
              </form>
            )}
          </div>
        </div>
      </div>
      {showTerms && <TermsModal onClose={() => setShowTerms(false)} />}
      {showPrivacy && <PrivacyModal onClose={() => setShowPrivacy(false)} />}
    </section>
  );
}